import type {TaskComment, TaskParticipant} from '@api/tasks/types';

/** Максимальная длина комментария по контракту задач. */
export const TASK_COMMENT_MAX_LENGTH = 5000;

/** Фрагменты текста для визуальной подсветки; адресатов уведомления не определяет. */
export const splitCommentMentions = (text: string, names: string[]) => {
  const labels = [...new Set(names.filter(Boolean))]
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (labels.length === 0) return text ? [{text, mention: false}] : [];
  const alternatives = labels.join('|');
  const pattern = new RegExp(
    `(^|[\\s([{])(@(?:${alternatives}))(?![\\p{L}\\p{N}_])`,
    'gu',
  );
  const parts: {text: string; mention: boolean}[] = [];
  let offset = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index + match[1].length;
    if (start > offset)
      parts.push({text: text.slice(offset, start), mention: false});
    parts.push({text: match[2], mention: true});
    offset = start + match[2].length;
  }
  if (offset < text.length)
    parts.push({text: text.slice(offset), mention: false});
  return parts;
};

/** Полные имена для подсветки черновика; не изменяет список адресатов. */
export const commentMentionRanges = (text: string, names: string[]) => {
  const ranges: {start: number; end: number}[] = [];
  let offset = 0;
  for (const part of splitCommentMentions(text, names)) {
    if (part.mention)
      ranges.push({start: offset, end: offset + part.text.length});
    offset += part.text.length;
  }
  return ranges;
};

/** Выбранное упоминание связано с ID и точным диапазоном текста. */
export interface MentionToken {
  userSid: string;
  displayName: string;
  start: number;
  end: number;
}
/** Диапазон редактирования до изменения textarea. */
export interface MentionEditRange {
  start: number;
  end: number;
}

/** Удаляет только первое полное @имя; остальной текст и пробелы сохраняются. */
export const removeFirstCommentMention = (
  text: string,
  displayName: string,
) => {
  let start = 0;
  for (const part of splitCommentMentions(text, [displayName])) {
    if (part.mention) {
      const end = start + part.text.length;
      return {
        text: text.slice(0, start) + text.slice(end),
        range: {start, end},
      };
    }
    start += part.text.length;
  }
  return {text, range: undefined};
};

/** Восстановление токенов из серверных ID; неоднозначные/нетекстовые адресаты сохраняются отдельно. */
export const restoreCommentMentions = (comment?: TaskComment) => {
  const people = (comment?.mentions ?? []).map((userSid) => ({
    userSid,
    displayName:
      comment?.mentionUsers?.find((person) => person.userSid === userSid)
        ?.displayName ?? userSid,
  }));
  const tokens: MentionToken[] = [];
  let offset = 0;
  for (const part of splitCommentMentions(
    comment?.text ?? '',
    people.map((person) => person.displayName),
  )) {
    const matches = people.filter(
      (person) => part.text === `@${person.displayName}`,
    );
    if (part.mention && matches.length === 1)
      tokens.push({
        ...matches[0],
        start: offset,
        end: offset + part.text.length,
      });
    offset += part.text.length;
  }
  const detached: TaskParticipant[] = people.filter(
    (person) => !tokens.some((token) => token.userSid === person.userSid),
  );
  return {tokens, detached};
};

/** PATCH не трогает адресатов, если их набор не изменился в редакторе. */
export const changedCommentMentions = (
  original: string[],
  current: string[],
) => {
  const unique = [...new Set(current)];
  return unique.length === new Set(original).size &&
    unique.every((sid) => original.includes(sid))
    ? undefined
    : unique;
};

/** Изменённый токен теряет адресата; неизменённые диапазоны сдвигаются вместе с текстом. */
export const reconcileMentionTokens = (
  before: string,
  after: string,
  tokens: MentionToken[],
  range?: MentionEditRange,
): MentionToken[] => {
  if (before === after) return tokens;
  let start = 0;
  let oldEnd = before.length;
  let newEnd = after.length;
  const delta = after.length - before.length;
  const exactRange =
    range &&
    range.start >= 0 &&
    range.end + delta >= range.start &&
    before.slice(0, range.start) === after.slice(0, range.start) &&
    before.slice(range.end) === after.slice(range.end + delta);
  if (exactRange) {
    start = range.start;
    oldEnd = range.end;
    newEnd = oldEnd + delta;
  } else {
    while (
      start < before.length &&
      start < after.length &&
      before[start] === after[start]
    )
      start++;
    while (
      oldEnd > start &&
      newEnd > start &&
      before[oldEnd - 1] === after[newEnd - 1]
    ) {
      oldEnd--;
      newEnd--;
    }
  }
  return tokens
    .flatMap((token) => {
      // При неоднозначном текстовом diff тёзок нельзя угадать удалённого адресата.
      if (
        !exactRange &&
        tokens.some(
          (other) =>
            other.userSid !== token.userSid &&
            other.displayName === token.displayName,
        )
      )
        return [];
      if (token.end <= start) return [token];
      if (token.start >= oldEnd)
        return [{...token, start: token.start + delta, end: token.end + delta}];
      return [];
    })
    .filter(
      (token) =>
        after.slice(token.start, token.end) === `@${token.displayName}` &&
        !/[\p{L}\p{N}_]/u.test(after[token.end] ?? '') &&
        (token.start === 0 || /[\s([{]/u.test(after[token.start - 1])),
    );
};
